import { ApolloError } from '@apollo/client';
import { FormLeavingListener } from '@exam-center/components';
import { useExamCenter } from '@exam-center/exam-center-details';
import {
  GetExamCenterProctorRequestByIdDocument,
  GetExamCenterProctorRequestsCountDocument,
  SearchExamCenterInReviewProctorRequestsDocument,
  SearchExamCenterRejectedProctorRequestsDocument,
  useSaveExamCenterProctorRequestAsRejectedMutation,
  useSubmitExamCenterProctorRequestForReviewMutation,
} from '@exam-center/graphql';
import { PROCTOR_TYPE_TEXTS } from '@exam-center/proctor-management';
import {
  ALLOW_FILE_TYPES,
  DownloadProctorAgreementFormHelperText,
  groupNewAndExistingFiles,
  mapExamCenterProctorRecordErrorCodeToFormError,
  mapFileFormInputToStorageObject,
  ProctorRequestFormFields,
  ProctorRequestSubmissionModal,
  ProctorType,
  useGetProctorRequestDownloadSignedUrlWithInfo,
  useUploadProctorRequestFiles,
} from '@exam-center/proctor-request-management';
import { useNavigate } from '@exam-center/router';
import { ID_CARD_NUMBER_MASK, PHONE_NUMBER_MASK } from '@exam-center/utils';
import { Button, Flex, Form, Text } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import {
  ENGLISH_TITLE_OPTIONS,
  isEnglishTitle,
  isThaiTitle,
  THAI_TITLE_OPTIONS,
} from '@shared/utils';
import IMask from 'imask';
import { useEffect } from 'react';

import {
  REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME,
  type RejectedProctorRequestEditionFormInput,
  type SignedUrlInput,
  useProctorRequestDetails,
  useRejectedProctorRequestEditionForm,
} from '../../providers';
import { mapStorageObjectToFormFileInput } from '../draft-proctor-request-edition-form-logic/mapper';
import { ProctorRequestRejectedVerdict } from '../proctor-request-rejected-verdict';

import { createRejectedExamCenterProctorRequestPayload } from './utils';

export default function RejectedProctorRequestEditionFormLogic() {
  const { proctorRequest } = useProctorRequestDetails();
  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useRejectedProctorRequestEditionForm();
  const { examCenter } = useExamCenter();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [isSubmissionModalOpen, { open: openSubmissionModal, close: closeSubmissionModal }] =
    useDisclosure(false);

  const agreementFiles = useGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: proctorRequest.id,
    proctorRequestStorageObject: proctorRequest?.agreementStorageObjects ?? [],
    options: { skip: !proctorRequest },
  });

  const personalInfoFiles = useGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: proctorRequest.id,
    proctorRequestStorageObject: proctorRequest.personalInfoStorageObjects ?? [],
    options: { skip: !proctorRequest },
  });

  useEffect(() => {
    if (proctorRequest) {
      const maskedPhoneNumber = IMask.createMask({ mask: PHONE_NUMBER_MASK });
      maskedPhoneNumber.resolve(proctorRequest.phoneNumber ?? '');

      const maskedIdNumber = IMask.createMask({ mask: ID_CARD_NUMBER_MASK });
      maskedIdNumber.resolve(proctorRequest.idNumber);

      const mappedAgreementFiles = mapStorageObjectToFormFileInput(agreementFiles);
      const mappedPersonalInfoFiles = mapStorageObjectToFormFileInput(personalInfoFiles);

      reset({
        personal: {
          idNumber: maskedIdNumber.value,
          titleTh: isThaiTitle(proctorRequest.titleTh)
            ? { value: proctorRequest.titleTh, label: proctorRequest.titleTh }
            : undefined,
          firstNameTh: proctorRequest.firstNameTh,
          lastNameTh: proctorRequest.lastNameTh,
          titleEn: isEnglishTitle(proctorRequest.titleEn)
            ? { value: proctorRequest.titleEn, label: proctorRequest.titleEn }
            : undefined,
          firstNameEn: proctorRequest.firstNameEn,
          lastNameEn: proctorRequest.lastNameEn,
          email: proctorRequest.email,
          phoneNumber: maskedPhoneNumber.value,
          address: proctorRequest.address ?? '',
        },
        employeeInfo: {
          code: proctorRequest.metadata?.employeeId,
          position: proctorRequest.metadata?.position,
          department: proctorRequest.metadata?.department,
          termination: {
            isAssigned: proctorRequest.endedAt ? 'true' : 'false',
            endedAt: proctorRequest.endedAt ?? null,
          },
          startedAt: proctorRequest.startedAt,
        },
        document: {
          agreementForms: mappedAgreementFiles,
          identifications: mappedPersonalInfoFiles,
        },
      });
    }
  }, [agreementFiles, personalInfoFiles, proctorRequest, reset]);

  const shouldDisplayEndDate = watch('employeeInfo.termination.isAssigned') === 'true';

  useEffect(() => {
    if (!shouldDisplayEndDate) {
      setValue(REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.termination.endedAt, null);
    }
  }, [shouldDisplayEndDate, setValue]);

  const [saveRejectedProctorRequest, { loading: isSavingRequest }] =
    useSaveExamCenterProctorRequestAsRejectedMutation();
  const [submitProctorRequestForReview, { loading: isSubmittingRequest }] =
    useSubmitExamCenterProctorRequestForReviewMutation();

  const { handleUpload } = useUploadProctorRequestFiles({ requestId: proctorRequest.id });

  const handleSaveDraftProctorRequest = async (data: RejectedProctorRequestEditionFormInput) => {
    const { agreementForms, identifications } = data.document;
    const { newFiles: newAgreementFiles, existingFiles: existingAgreementFiles } =
      groupNewAndExistingFiles<SignedUrlInput>(agreementForms);

    const { newFiles: newIdentificationFiles, existingFiles: existingIdentificationFiles } =
      groupNewAndExistingFiles<SignedUrlInput>(identifications);

    const uploadedAgreementFiles = await handleUpload(newAgreementFiles);
    const uploadedIdentificationFiles = await handleUpload(newIdentificationFiles);

    const mappedAgreementFiles = [
      ...uploadedAgreementFiles,
      ...existingAgreementFiles.map((file) => mapFileFormInputToStorageObject(file)),
    ];

    const mappedPersonalInfoFiles = [
      ...uploadedIdentificationFiles,
      ...existingIdentificationFiles.map((file) => mapFileFormInputToStorageObject(file)),
    ];

    const payload = createRejectedExamCenterProctorRequestPayload(
      proctorRequest.id,
      data,
      mappedAgreementFiles,
      mappedPersonalInfoFiles,
      proctorRequest.examCenterCode,
    );

    return await saveRejectedProctorRequest({
      variables: {
        saveExamCenterProctorRequestAsRejectedInput: payload,
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetExamCenterProctorRequestByIdDocument],
      fetchPolicy: 'network-only',
    });
  };

  const saveRejectedAndSubmitRequest = handleSubmit(
    async (data: RejectedProctorRequestEditionFormInput) => {
      try {
        await handleSaveDraftProctorRequest(data);
        await submitProctorRequestForReview({
          variables: {
            submitExamCenterProctorRequestInput: { id: proctorRequest.id },
          },
          onCompleted: () => {
            showSnackbar({
              message: 'ส่งคำขอผู้คุมสอบเรียบร้อยแล้ว',
              variant: 'success',
            });
            navigate(
              {
                pathname: '/:examCenterCode/exam-proctors/requests',
                search: '?status=IN_REVIEW',
              },
              {
                params: {
                  examCenterCode: examCenter.code,
                },
              },
            );
          },
          refetchQueries: [
            SearchExamCenterInReviewProctorRequestsDocument,
            SearchExamCenterRejectedProctorRequestsDocument,
            GetExamCenterProctorRequestsCountDocument,
          ],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        if (error instanceof ApolloError) {
          const graphQLError = error?.graphQLErrors?.[0]?.extensions?.details;
          const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
          const errorCodes = extensionDetails.map((detail) => detail.code);
          const formErrors = mapExamCenterProctorRecordErrorCodeToFormError(errorCodes);

          for (const formError of formErrors) {
            setError(formError.fieldName, {
              type: 'manual',
              message: formError.message,
            });
          }
        }
        showSnackbar({
          message: 'ไม่สามารถส่งคำขออนุมัติได้',
          variant: 'error',
        });
      } finally {
        closeSubmissionModal();
      }
    },
  );

  const handleRejectedSubmit = () => {
    openSubmissionModal();
  };

  const minEndAtDate = watch(REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.startedAt);

  const isSubmitRequestLoading = isSubmitting || isSavingRequest || isSubmittingRequest;

  return (
    <>
      <Form>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          <ProctorRequestFormFields
            fields={{
              proctorType: {
                mode: 'view',
                value: <Text>{PROCTOR_TYPE_TEXTS[proctorRequest.type]}</Text>,
              },
              personal: {
                idNumber: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.idNumber,
                },
                titleTh: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.titleTh,
                  slotProps: { options: THAI_TITLE_OPTIONS },
                },
                firstNameTh: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.firstNameTh,
                },
                lastNameTh: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.lastNameTh,
                },
                titleEn: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.titleEn,
                  slotProps: { options: ENGLISH_TITLE_OPTIONS },
                },
                firstNameEn: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.firstNameEn,
                },
                lastNameEn: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.lastNameEn,
                },
                email: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.email,
                },
                phoneNumber: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.phoneNumber,
                },
                address: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.address,
                },
              },
              employeeInfo: {
                code: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.code,
                },
                position: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.position,
                },
                department: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.department,
                },
                startedAt: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.startedAt,
                },
                isTerminationAssign: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.termination
                    .isAssigned,
                },
                endedAt: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.termination.endedAt,
                  slotProps: {
                    minDate: minEndAtDate,
                  },
                },
              },
              documents: {
                agreementForms: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.document.agreementForms,
                  slotProps: {
                    maxFiles: 4,
                    accept: ALLOW_FILE_TYPES,
                    helperText: (
                      <DownloadProctorAgreementFormHelperText
                        proctorType={ProctorType.TYPE_ONE_AND_TWO}
                      />
                    ),
                  },
                },
                identifications: {
                  mode: 'input',
                  name: REJECTED_PROCTOR_REQUEST_FORM_FIELD_NAME.document.identifications,
                  slotProps: {
                    maxFiles: 4,
                    accept: ALLOW_FILE_TYPES,
                  },
                },
              },
            }}
            options={{
              shouldShowEndedAtDateDisplay: shouldDisplayEndDate,
            }}
          />
          <ProctorRequestRejectedVerdict />
          <Flex sx={(theme) => ({ ml: 'auto', gap: theme.spacing(2.5) })}>
            <Button
              size='large'
              color='primary'
              onClick={handleRejectedSubmit}
              disabled={isSubmitRequestLoading}
            >
              ส่งคำขออนุมัติอีกครั้ง
            </Button>
          </Flex>
        </Flex>
      </Form>
      <FormLeavingListener onSubmit={() => saveRejectedAndSubmitRequest()} />
      <ProctorRequestSubmissionModal
        open={isSubmissionModalOpen}
        onAccept={saveRejectedAndSubmitRequest}
        onClose={closeSubmissionModal}
        onDiscard={closeSubmissionModal}
        loading={isSubmitRequestLoading}
      />
    </>
  );
}
